import { api, ACQUISITION_GOAL_PARAMS } from './config';
import type { AcquisitionGoalOverrides, Device, Location, CreateLocationData, Product, BeaconAssignment, LocationAssignment, CreateAssignmentData, AssignmentFilters, Organization, Mesh, EdgeAccountDigest, DigestFilters, DigestAttention, DigestSettings, DigestSettingsUpdate, ProductEquivalency, ProductEquivalencyMembership, LocationProductEquivalency, LocationInventory, LocationDevicesResponse, LocationOrderFulfillmentResponse, LocationHvaRateResponse, LocationEmailOpensResponse, LocationOpenQuotesResponse, LocationReportViews, Manufacturer, ProductEquivalencyCategory, PaginationParams, PaginatedResponse, DeviceSignalView, FleetDashboardPayload, InternalOpsDashboardPayload, DeviceComment, DeviceCommentsResponse, EdgeOnboardCustomerUser, EdgeOnboardCustomerUserInput, EdgeOnboardCustomerUserUpdate, EdgeOnboardGoLive, EdgeOnboardGoLiveDetail, EdgeOnboardGoLiveState, EdgeOnboardSettings, EdgeOnboardSlackMention, EdgeOnboardSubmission, EdgeOnboardTagSlip } from './config';

// Used by getAll methods to fetch complete datasets (dropdowns, unconverted pages).
// Backend defaults to 50 per page and caps at MAX_PAGE_SIZE=1000.
// 1000 is sufficient for all current dropdown use cases; entities that grow beyond
// this should be converted to server-side autocomplete/search.
const ALL_RECORDS = { per_page: '1000' };

// Helper to build params object with pagination
function withPagination(
  filters: Record<string, string | undefined>,
  pagination?: PaginationParams
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params[key] = value;
  }
  if (pagination?.page) params.page = String(pagination.page);
  if (pagination?.per_page) params.per_page = String(pagination.per_page);
  if (pagination?.sort_by) params.sort_by = pagination.sort_by;
  if (pagination?.sort_dir) params.sort_dir = pagination.sort_dir;
  if (pagination?.filter_field) params.filter_field = pagination.filter_field;
  if (pagination?.filter_op) params.filter_op = pagination.filter_op;
  if (pagination?.filter_value) params.filter_value = pagination.filter_value;
  return params;
}

// Organizations API
export const organizationsApi = {
  getAll: () => api.get<PaginatedResponse<Organization>>('/organizations', { params: ALL_RECORDS }).then(res => res.data.data),
  getOne: (id: string | number) => api.get<Organization>(`/organizations/${id}`).then(res => res.data),
  create: (data: Omit<Organization, 'id' | 'created_at' | 'updated_at' | 'total_locations' | 'active_locations' | 'inactive_locations'>) =>
    api.post<Organization>('/organizations', { organization: data }).then(res => res.data),
  update: (id: string | number, data: Partial<Organization>) =>
    api.put<Organization>(`/organizations/${id}`, { organization: data }).then(res => res.data),
  delete: (id: string | number) => api.delete(`/organizations/${id}`).then(res => res.data),
};

// Meshes API (sc-6059). Read-only since sc-6111: meshes mirror CRM districts
// and the nightly sync is the only writer — the write endpoints are gone.
export const meshesApi = {
  getAll: () =>
    api.get<PaginatedResponse<Mesh>>('/meshes', { params: ALL_RECORDS }).then(res => res.data.data),
};

// Locations API
export const locationsApi = {
  getAll: (filters?: { organization_id?: string | number }) => {
    const params: Record<string, any> = { ...ALL_RECORDS };
    if (filters?.organization_id) params.organization_id = filters.organization_id;
    return api.get<PaginatedResponse<Location>>('/locations', { params }).then(res => res.data.data);
  },
  getPage: (filters?: { organization_id?: string | number; hexahq_account_id?: string | number; name?: string }, pagination?: PaginationParams) => {
    const filterParams: Record<string, string | undefined> = {};
    if (filters?.organization_id) filterParams.organization_id = String(filters.organization_id);
    if (filters?.hexahq_account_id) filterParams.hexahq_account_id = String(filters.hexahq_account_id);
    if (filters?.name) filterParams.name = filters.name;
    return api.get<PaginatedResponse<Location>>('/locations', { params: withPagination(filterParams, pagination) }).then(res => res.data);
  },
  getAllWithoutFilters: () => api.get<PaginatedResponse<Location>>('/locations', { params: ALL_RECORDS }).then(res => res.data.data),
  getOne: (id: string | number) => api.get<Location>(`/locations/${id}`).then(res => res.data),
  create: (data: CreateLocationData) =>
    api.post<Location>('/locations', { location: data }).then(res => res.data),
  update: (id: string | number, data: Partial<Location>) =>
    api.put<Location>(`/locations/${id}`, { location: data }).then(res => res.data),
  delete: (id: string | number) => api.delete(`/locations/${id}`).then(res => res.data),
  getAssignedProducts: (id: string | number) =>
    api.get<LocationAssignment[]>(`/locations/${id}/assigned_products`).then(res => res.data),
  getInventory: (id: string | number) =>
    api.get<LocationInventory>(`/locations/${id}/inventory`).then(res => res.data),
  getDevices: (id: string | number) =>
    api.get<LocationDevicesResponse>(`/locations/${id}/devices`).then(res => res.data.devices),
  getOrderFulfillment: (id: string | number) =>
    api.get<LocationOrderFulfillmentResponse>(`/locations/${id}/order_fulfillment`).then(res => res.data),
  getHvaRate: (id: string | number) =>
    api.get<LocationHvaRateResponse>(`/locations/${id}/hva_rate`).then(res => res.data),
  getEmailOpens30d: (id: string | number) =>
    api.get<LocationEmailOpensResponse>(`/locations/${id}/email_opens_30d`).then(res => res.data),
  getOpenQuotes: (id: string | number) =>
    api.get<LocationOpenQuotesResponse>(`/locations/${id}/open_quotes`).then(res => res.data.quotes),
  getReportViews30d: (id: string | number) =>
    api.get<LocationReportViews>(`/locations/${id}/report_views_30d`).then(res => res.data),
  // Onboarding print kit [sc-6305]: blob GET via the shared axios instance so it
  // carries the bearer token (a raw browser download would not). Returns the
  // bytes + the server's filename for an object-URL download.
  onboardingKitPdf: async (
    id: string | number,
    piece: 'sheet' | 'cards',
  ): Promise<{ blob: Blob; filename: string }> => {
    const res = await api.get(`/locations/${id}/onboarding_${piece}.pdf`, { responseType: 'blob' });
    const disposition = (res.headers['content-disposition'] as string | undefined) ?? '';
    // Quoted form first; the unquoted branch stops at ';' so it can't swallow a
    // following filename*= parameter.
    const match = disposition.match(/filename="([^"]+)"/) ?? disposition.match(/filename=([^;]+)/);
    return { blob: res.data as Blob, filename: match?.[1]?.trim() ?? `edge-onboarding-${piece}-${id}.pdf` };
  },
};

// Devices API
export const devicesApi = {
  getAll: (filters?: { location_id?: number; unassigned?: boolean }) => {
    const params: Record<string, string> = { ...ALL_RECORDS };
    if (filters?.location_id) params.location_id = String(filters.location_id);
    if (filters?.unassigned) params.unassigned = 'true';
    return api.get<PaginatedResponse<Device>>('/devices', { params }).then(res => res.data.data).catch((err) => {
      if (err?.response?.status === 404) return [];
      throw err;
    });
  },
  getPage: (filters?: { location_id?: number; unassigned?: boolean }, pagination?: PaginationParams) => {
    const filterParams: Record<string, string | undefined> = {};
    if (filters?.location_id) filterParams.location_id = String(filters.location_id);
    if (filters?.unassigned) filterParams.unassigned = 'true';
    return api.get<PaginatedResponse<Device>>('/devices', { params: withPagination(filterParams, pagination) }).then(res => res.data);
  },
  getOne: (id: number) => api.get<Device>(`/devices/${id}`).then(res => res.data),
  getSignalView: (id: number) =>
    api.get<DeviceSignalView>(`/devices/${id}/signal_view`).then(res => res.data),
  create: (data: Partial<Device>) =>
    api.post<Device>('/devices', { device: data }).then(res => res.data),
  update: (id: number, data: Partial<Device>) =>
    api.put<Device>(`/devices/${id}`, { device: data }).then(res => res.data),
  // sc-6272: hygiene delete — removes the record (the backend refuses devices
  // with diagnostics history; that 422 surfaces via extractApiError).
  delete: (id: number) => api.delete(`/devices/${id}`).then(res => res.data),
  // sc-6397: queues a one-shot restart through the gateway-commands ledger.
  // A Particle failure (502) or an unconfigured env (503) surfaces via extractApiError.
  queueRestart: (id: number) =>
    api.post<{ restart_after: number; comment: DeviceComment }>(`/devices/${id}/queue_restart`).then(res => res.data),
};

// Products API
export const productsApi = {
  getAll: () => api.get<PaginatedResponse<Product>>('/products', { params: ALL_RECORDS }).then(res =>
    res.data.data.map(product => ({
      ...product,
      unit_cost: product.unit_cost != null ? Number(product.unit_cost) : null,
    }))
  ),
  getPage: (filters?: Record<string, never>, pagination?: PaginationParams) => {
    return api.get<PaginatedResponse<Product>>('/products', { params: withPagination({}, pagination) }).then(res => ({
      ...res.data,
      data: res.data.data.map(product => ({
        ...product,
        unit_cost: product.unit_cost != null ? Number(product.unit_cost) : null,
      })),
    }));
  },
  getOne: (id: string) => api.get<Product>(`/products/${id}`).then(res => res.data),
  create: (data: Partial<Product> & { product_equivalency_category_id?: number; product_equivalency_id?: number; hide_when_zero?: boolean }) => {
    const { product_equivalency_category_id, product_equivalency_id, hide_when_zero, ...productData } = data;
    return api.post<Product>('/products', { product: productData, product_equivalency_category_id, product_equivalency_id, hide_when_zero }).then(res => res.data);
  },
  update: (id: string, data: Partial<Product> & { product_equivalency_category_id?: number }) => {
    const { product_equivalency_category_id, ...productData } = data;
    return api.put<Product>(`/products/${id}`, { product: productData, product_equivalency_category_id }).then(res => res.data);
  },
  delete: (id: string) => api.delete(`/products/${id}`).then(res => res.data),
  getAssignedLocations: (sku: string) =>
    api.get<LocationAssignment[]>(`/products/${sku}/assigned_locations`).then(res => res.data),
  checkSku: (sku: string) =>
    api.get<{ exists: boolean }>('/products/check_sku', { params: { sku } }).then(res => res.data),
};

// Location Assignments API
export const locationAssignmentsApi = {
  getAll: (filters?: AssignmentFilters) =>
    api.get<PaginatedResponse<LocationAssignment>>('/location_product_assignments', { params: { ...ALL_RECORDS, ...filters } }).then(res => res.data.data),
  getPage: (filters?: AssignmentFilters, pagination?: PaginationParams) => {
    const filterParams: Record<string, string | undefined> = {};
    if (filters?.location_id) filterParams.location_id = String(filters.location_id);
    if (filters?.sku) filterParams.sku = filters.sku;
    if (filters?.status) filterParams.status = filters.status;
    if (filters?.organization_id) filterParams.organization_id = String(filters.organization_id);
    return api.get<PaginatedResponse<LocationAssignment>>('/location_product_assignments', { params: withPagination(filterParams, pagination) }).then(res => res.data);
  },
  getOne: (id: number) =>
    api.get<LocationAssignment>(`/location_product_assignments/${id}`).then(res => res.data),
  create: (data: CreateAssignmentData) =>
    api.post<LocationAssignment>('/location_product_assignments', { location_product_assignment: data }).then(res => res.data),
  update: (id: number, data: Partial<LocationAssignment>) =>
    api.put<LocationAssignment>(`/location_product_assignments/${id}`, { location_product_assignment: data }).then(res => res.data),
  delete: (id: number) =>
    api.delete(`/location_product_assignments/${id}`).then(res => res.data),
  deactivate: (id: number, reason: string) =>
    api.post<LocationAssignment>(`/location_product_assignments/${id}/deactivate`, { reason }).then(res => res.data),
  reactivate: (id: number) =>
    api.post<LocationAssignment>(`/location_product_assignments/${id}/reactivate`).then(res => res.data),
  discontinue: (id: number, reason: string) =>
    api.post<LocationAssignment>(`/location_product_assignments/${id}/discontinue`, { reason }).then(res => res.data),
  bulkCreate: (assignments: CreateAssignmentData[]) =>
    api.post<{ assignments: LocationAssignment[] }>('/location_product_assignments/bulk_create', { assignments }).then(res => res.data),
  getActive: (filters?: AssignmentFilters) =>
    api.get<LocationAssignment[]>('/location_product_assignments/active', { params: filters }).then(res => res.data),
  getNeedingRestock: (filters?: AssignmentFilters) =>
    api.get<LocationAssignment[]>('/location_product_assignments/needing_restock', { params: filters }).then(res => res.data),
  getByLocation: (locationId: string | number, filters?: AssignmentFilters) =>
    api.get<LocationAssignment[]>(`/locations/${locationId}/product_assignments`, { params: filters }).then(res => res.data),
  createForLocation: (locationId: string | number, data: Omit<CreateAssignmentData, 'location_id'>) =>
    api.post<LocationAssignment>(`/locations/${locationId}/product_assignments`, { location_product_assignment: { ...data, location_id: locationId } }).then(res => res.data),
  getByProduct: (sku: string, filters?: AssignmentFilters) =>
    api.get<LocationAssignment[]>(`/products/${sku}/location_assignments`, { params: filters }).then(res => res.data),
  createForProduct: (sku: string, data: Omit<CreateAssignmentData, 'sku'>) =>
    api.post<LocationAssignment>(`/products/${sku}/location_assignments`, { location_product_assignment: { ...data, sku } }).then(res => res.data),
  // Returns counts of unique locations with active assignments, keyed by SKU
  getCounts: () =>
    api.get<Record<string, number>>('/location_product_assignments/counts').then(res => res.data),
  checkExists: (locationId: number, sku: string) =>
    api.get<{ exists: boolean }>('/location_product_assignments/exists', { params: { location_id: locationId, sku } }).then(res => res.data.exists),
  // Returns counts of active assignments per location, keyed by location ID
  getCountsByLocation: () =>
    api.get<Record<string, number>>('/locations/assignment_counts').then(res => res.data),
};

// Beacon Assignments API
export const beaconAssignmentsApi = {
  getAll: (filters?: { sku?: string; location_id?: number; unassigned_only?: boolean }) => {
    const params: Record<string, string> = { ...ALL_RECORDS };
    if (filters?.sku) params.sku = filters.sku;
    if (filters?.location_id) params.location_id = String(filters.location_id);
    if (filters?.unassigned_only) params.unassigned_only = 'true';
    return api.get<PaginatedResponse<BeaconAssignment>>('/beacon_assignments', { params }).then(res => res.data.data);
  },
  getPage: (filters?: { sku?: string; location_id?: number; unassigned_only?: boolean }, pagination?: PaginationParams) => {
    const filterParams: Record<string, string | undefined> = {};
    if (filters?.sku) filterParams.sku = filters.sku;
    if (filters?.location_id) filterParams.location_id = String(filters.location_id);
    if (filters?.unassigned_only) filterParams.unassigned_only = 'true';
    return api.get<PaginatedResponse<BeaconAssignment>>('/beacon_assignments', { params: withPagination(filterParams, pagination) }).then(res => res.data);
  },
  getOne: (id: string) => api.get<BeaconAssignment>(`/beacon_assignments/${id}`).then(res => res.data),
  getBySku: (sku: string) => api.get<PaginatedResponse<BeaconAssignment>>('/beacon_assignments', { params: { sku, ...ALL_RECORDS } }).then(res => res.data.data),

  // Location-scoped queries
  atLocation: (locationId: number, options?: { sku?: string; activeOnly?: boolean }) => {
    const params: Record<string, string> = { location_id: String(locationId) };
    if (options?.sku) params.sku = options.sku;
    if (options?.activeOnly) params.active_only = 'true';
    return api.get<BeaconAssignment[]>('/beacon_assignments/at_location', { params }).then(res => res.data);
  },
  availableForLocation: (sku: string) =>
    api.get<BeaconAssignment[]>('/beacon_assignments/available_for_location', { params: { sku } }).then(res => res.data),

  // CRUD operations
  create: (data: Omit<BeaconAssignment, 'created_at' | 'updated_at'>) =>
    api.post<BeaconAssignment>('/beacon_assignments', { beacon_assignment: data }).then(res => res.data),
  bulkCreate: (assignments: Omit<BeaconAssignment, 'created_at' | 'updated_at'>[]) =>
    api.post<{ assignments: BeaconAssignment[]; errors?: { index: number; beacon_id: string; errors: string[] }[] }>('/beacon_assignments/bulk_create', { assignments }).then(res => res.data),
  update: (id: string, data: Partial<BeaconAssignment>) =>
    api.put<BeaconAssignment>(`/beacon_assignments/${id}`, { beacon_assignment: data }).then(res => res.data),
  delete: (id: string) => api.delete(`/beacon_assignments/${id}`).then(res => res.data),
  bulkDelete: (ids: string[]) =>
    api.post<{ deleted_count: number }>('/beacon_assignments/bulk_delete', { beacon_ids: ids }).then(res => res.data),

  // Location assignment operations
  assignToLocation: (beaconId: string, locationId: number, performedBy?: string, notes?: string) =>
    api.post<BeaconAssignment>(`/beacon_assignments/${beaconId}/assign_to_location`, {
      location_id: locationId,
      performed_by: performedBy,
      notes,
    }).then(res => res.data),
  unassignFromLocation: (beaconId: string, performedBy?: string, notes?: string) =>
    api.post<BeaconAssignment>(`/beacon_assignments/${beaconId}/unassign_from_location`, {
      performed_by: performedBy,
      notes,
    }).then(res => res.data),
  bulkAssignToLocation: (beaconIds: string[], locationId: number, performedBy?: string, notes?: string) =>
    api.post<{ assignments: BeaconAssignment[]; errors?: any[] }>('/beacon_assignments/bulk_assign_to_location', {
      beacon_ids: beaconIds,
      location_id: locationId,
      performed_by: performedBy,
      notes,
    }).then(res => res.data),
  bulkUnassignFromLocation: (beaconIds: string[], performedBy?: string, notes?: string) =>
    api.post<{ assignments: BeaconAssignment[]; errors?: any[] }>('/beacon_assignments/bulk_unassign_from_location', {
      beacon_ids: beaconIds,
      performed_by: performedBy,
      notes,
    }).then(res => res.data),

  // Atomic reassignment (single transaction, single history row).
  reassignToLocation: (beaconId: string, locationId: number, performedBy?: string, notes?: string) =>
    api.post<BeaconAssignment>(`/beacon_assignments/${beaconId}/reassign_to_location`, {
      location_id: locationId,
      performed_by: performedBy,
      notes,
    }).then(res => res.data),
  bulkReassignToLocation: (beaconIds: string[], locationId: number, performedBy?: string, notes?: string) =>
    api.post<{ assignments: BeaconAssignment[]; errors?: any[] }>('/beacon_assignments/bulk_reassign_to_location', {
      beacon_ids: beaconIds,
      location_id: locationId,
      performed_by: performedBy,
      notes,
    }).then(res => res.data),

  // Lifecycle operations
  retire: (beaconId: string, reason?: string, performedBy?: string) =>
    api.post<BeaconAssignment>(`/beacon_assignments/${beaconId}/retire`, {
      reason,
      performed_by: performedBy,
    }).then(res => res.data),
  reactivate: (beaconId: string, performedBy?: string, notes?: string) =>
    api.post<BeaconAssignment>(`/beacon_assignments/${beaconId}/reactivate`, {
      performed_by: performedBy,
      notes,
    }).then(res => res.data),
};

// Beacon Concerns API

export type BeaconConcernType =
  | 'unassigned_visitor'
  | 'misassigned_beacon'
  | 'lpa_config_gap'
  | 'sku_drift';

export type BeaconConcernStatus = 'open' | 'acknowledged' | 'resolved';
export type BeaconConcernResolutionMode = 'auto' | 'manual_fixed' | 'manual_dismissed';

export interface BeaconConcernHistoryEvent {
  at: string;
  event: 'opened' | 'reopened' | 'acknowledged' | 'resolved';
  actor?: string;
  note?: string;
  resolution_mode?: BeaconConcernResolutionMode;
}

export interface BeaconConcern {
  id: number;
  concern_type: BeaconConcernType;
  beacon_id: string;
  location_id: number;
  sku: string;
  status: BeaconConcernStatus;
  first_detected_at: string;
  last_observed_at: string;
  resolved_at?: string;
  resolution_mode?: BeaconConcernResolutionMode;
  resolution_note?: string;
  history: BeaconConcernHistoryEvent[];
  details: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface BeaconConcernFilters {
  type?: BeaconConcernType | '';
  status?: BeaconConcernStatus | '';
  location_id?: string;
  beacon_id?: string;
}

export interface BeaconConcernSummary {
  open: number;
  by_type: Record<BeaconConcernType, number>;
}

export const beaconConcernsApi = {
  list: (filters: BeaconConcernFilters = {}) =>
    api.get<PaginatedResponse<BeaconConcern>>('/beacon_concerns', {
      params: { ...ALL_RECORDS, ...filters },
    }).then(res => res.data.data),

  listPage: (filters: BeaconConcernFilters = {}, pagination?: PaginationParams) => {
    const filterParams: Record<string, string | undefined> = {};
    if (filters.type) filterParams.type = filters.type;
    if (filters.status) filterParams.status = filters.status;
    if (filters.beacon_id) filterParams.beacon_id = filters.beacon_id;
    if (filters.location_id) filterParams.location_id = filters.location_id;
    return api.get<PaginatedResponse<BeaconConcern>>('/beacon_concerns', {
      params: withPagination(filterParams, pagination),
    }).then(res => res.data);
  },

  getOne: (id: number) =>
    api.get<BeaconConcern>(`/beacon_concerns/${id}`).then(res => res.data),

  summary: () =>
    api.get<BeaconConcernSummary>('/beacon_concerns/summary').then(res => res.data),

  acknowledge: (id: number) =>
    api.post<BeaconConcern>(`/beacon_concerns/${id}/acknowledge`, {}).then(res => res.data),

  resolve: (id: number, mode: 'manual_fixed' | 'manual_dismissed', note: string) =>
    api.post<BeaconConcern>(`/beacon_concerns/${id}/resolve`, { mode, note }).then(res => res.data),
};

// Edge Account Digests API
export const edgeAccountDigestsApi = {
  getAll: (filters?: DigestFilters) =>
    api.get<PaginatedResponse<EdgeAccountDigest>>('/edge_account_digests', { params: { ...ALL_RECORDS, ...filters } }).then(res => res.data.data),
  getPage: (filters?: DigestFilters, pagination?: PaginationParams) => {
    const filterParams: Record<string, string | undefined> = {};
    if (filters?.status) filterParams.status = filters.status;
    if (filters?.location_id) filterParams.location_id = String(filters.location_id);
    if (filters?.start_date) filterParams.start_date = filters.start_date;
    if (filters?.end_date) filterParams.end_date = filters.end_date;
    if (filters?.section) filterParams.section = filters.section;
    if (filters?.exclude_attention) filterParams.exclude_attention = 'true';
    if (filters?.created_today) filterParams.created_today = 'true';
    return api.get<PaginatedResponse<EdgeAccountDigest>>('/edge_account_digests', { params: withPagination(filterParams, pagination) }).then(res => res.data);
  },
  getAttention: (filters?: DigestFilters) => {
    const params: Record<string, string> = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.location_id) params.location_id = String(filters.location_id);
    if (filters?.section) params.section = filters.section;
    if (filters?.created_today) params.created_today = 'true';
    return api.get<DigestAttention>('/edge_account_digests/attention', { params }).then(res => res.data);
  },
  getOne: (id: number) =>
    api.get<EdgeAccountDigest>(`/edge_account_digests/${id}`).then(res => res.data),
  // `override` (sc-6006): force the single approve past the overridable
  // outage/silent locks after the rep confirms the warning modal.
  approve: (id: number, override?: boolean) =>
    api.patch<EdgeAccountDigest>(
      `/edge_account_digests/${id}/approve`,
      override ? { override: true } : undefined,
    ).then(res => res.data),
  bulkApprove: (ids: number[]) =>
    api.post<{
      message: string;
      approved_count: number;
      errors: Array<{ digest_id: number; location_name?: string; error: string }>;
    }>('/edge_account_digests/bulk_approve', {
      digest_ids: ids,
    }).then(res => res.data),
  // `override` (sc-6006): force this single Send past the send-time outage
  // guard (threaded through to the email job's backstop re-check).
  send: (id: number, override?: boolean) =>
    api.post<EdgeAccountDigest>(
      `/edge_account_digests/${id}/send_email`,
      override ? { override: true } : {},
    ).then(res => res.data),
  cancel: (id: number) =>
    api.post<EdgeAccountDigest>(`/edge_account_digests/${id}/cancel`).then(res => res.data),
  restore: (id: number) =>
    api.post<EdgeAccountDigest>(`/edge_account_digests/${id}/restore`).then(res => res.data),
  sendApproved: () =>
    api.post<{ message: string; queued_count: number }>('/edge_account_digests/send_approved').then(res => res.data),
  delete: (id: number) =>
    api.delete(`/edge_account_digests/${id}`).then(res => res.data),
  bulkDelete: (ids: number[]) =>
    api.post<{ message: string; deleted_count: number; errors: any[] }>('/edge_account_digests/bulk_delete', {
      digest_ids: ids
    }).then(res => res.data),
  bulkMarkSent: (ids: number[]) =>
    api.post<{ message: string; marked_count: number; errors: any[] }>('/edge_account_digests/bulk_mark_sent', {
      digest_ids: ids
    }).then(res => res.data),
  // Global subject templates + per-type toggles (sc-6504). PATCH is a full
  // replace of all four types; the server 422s with { errors: { "<type>.<field>": [...] } }.
  settings: {
    get: () => api.get<DigestSettings>('/edge_account_digests/settings').then(res => res.data),
    update: (data: DigestSettingsUpdate) =>
      api.patch<DigestSettings>('/edge_account_digests/settings', data).then(res => res.data),
  },
};

// Product Equivalencies API
export const productEquivalenciesApi = {
  getAll: (filters?: { product_equivalency_category_id?: number }) =>
    api.get<PaginatedResponse<ProductEquivalency>>('/product_equivalencies', { params: { ...ALL_RECORDS, ...filters } }).then(res => res.data.data),
  getPage: (filters?: { product_equivalency_category_id?: number; search?: string }, pagination?: PaginationParams) => {
    const filterParams: Record<string, string | undefined> = {};
    if (filters?.product_equivalency_category_id) filterParams.product_equivalency_category_id = String(filters.product_equivalency_category_id);
    if (filters?.search) filterParams.search = filters.search;
    return api.get<PaginatedResponse<ProductEquivalency>>('/product_equivalencies', { params: withPagination(filterParams, pagination) }).then(res => res.data);
  },
  getOne: (id: number) =>
    api.get<ProductEquivalency>(`/product_equivalencies/${id}`).then(res => res.data),
  create: (data: Pick<ProductEquivalency, 'name' | 'hexa_hq_sku' | 'product_equivalency_category_id' | 'description'>) =>
    api.post<ProductEquivalency>('/product_equivalencies', { product_equivalency: data }).then(res => res.data),
  update: (id: number, data: Partial<Pick<ProductEquivalency, 'name' | 'hexa_hq_sku' | 'product_equivalency_category_id' | 'description'>>) =>
    api.put<ProductEquivalency>(`/product_equivalencies/${id}`, { product_equivalency: data }).then(res => res.data),
  delete: (id: number) =>
    api.delete(`/product_equivalencies/${id}`).then(res => res.data),
  getMembers: (id: number) =>
    api.get<ProductEquivalencyMembership[]>(`/product_equivalencies/${id}/members`).then(res => res.data),
  checkName: (name: string, categoryId: number, excludeId?: number) =>
    api.get<{ exists: boolean; id?: number; name?: string }>('/product_equivalencies/check_name', {
      params: { name, category_id: categoryId, exclude_id: excludeId },
    }).then(res => res.data),
};

// Manufacturers API
export const manufacturersApi = {
  getAll: () => api.get<PaginatedResponse<Manufacturer>>('/manufacturers', { params: ALL_RECORDS }).then(res => res.data.data),
  create: (data: { name: string }) =>
    api.post<Manufacturer>('/manufacturers', { manufacturer: data }).then(res => res.data),
  update: (id: number, data: Partial<{ name: string }>) =>
    api.put<Manufacturer>(`/manufacturers/${id}`, { manufacturer: data }).then(res => res.data),
  delete: (id: number) => api.delete(`/manufacturers/${id}`).then(res => res.data),
};

// Product Equivalency Categories API
export const productEquivalencyCategoriesApi = {
  getAll: () => api.get<PaginatedResponse<ProductEquivalencyCategory>>('/product_equivalency_categories', { params: ALL_RECORDS }).then(res => res.data.data),
  create: (data: { name: string }) =>
    api.post<ProductEquivalencyCategory>('/product_equivalency_categories', { product_equivalency_category: data }).then(res => res.data),
  update: (id: number, data: Partial<{ name: string }>) =>
    api.put<ProductEquivalencyCategory>(`/product_equivalency_categories/${id}`, { product_equivalency_category: data }).then(res => res.data),
  delete: (id: number) => api.delete(`/product_equivalency_categories/${id}`).then(res => res.data),
};

// Product Equivalency Memberships API
export const productEquivalencyMembershipsApi = {
  getAll: (filters?: { product_equivalency_id?: number }) =>
    api.get<PaginatedResponse<ProductEquivalencyMembership>>('/product_equivalency_memberships', { params: { ...ALL_RECORDS, ...filters } }).then(res => res.data.data),
  getPage: (filters?: { product_equivalency_id?: number }, pagination?: PaginationParams) => {
    const filterParams: Record<string, string | undefined> = {};
    if (filters?.product_equivalency_id) filterParams.product_equivalency_id = String(filters.product_equivalency_id);
    return api.get<PaginatedResponse<ProductEquivalencyMembership>>('/product_equivalency_memberships', { params: withPagination(filterParams, pagination) }).then(res => res.data);
  },
  create: (data: { product_equivalency_id: number; sku: string; hide_when_zero?: boolean }) =>
    api.post<ProductEquivalencyMembership>('/product_equivalency_memberships', { product_equivalency_membership: data }).then(res => res.data),
  update: (id: number, data: { hide_when_zero: boolean }) =>
    api.patch<ProductEquivalencyMembership>(`/product_equivalency_memberships/${id}`, { product_equivalency_membership: data }).then(res => res.data),
  delete: (id: number) =>
    api.delete(`/product_equivalency_memberships/${id}`).then(res => res.data),
};

// Location Product Equivalencies API
export const locationEquivalenciesApi = {
  getAll: (filters?: { location_id?: number; product_equivalency_id?: number }) =>
    api.get<PaginatedResponse<LocationProductEquivalency>>('/location_product_equivalencies', { params: { ...ALL_RECORDS, ...filters } }).then(res => res.data.data),
  getPage: (filters?: { location_id?: number; product_equivalency_id?: number }, pagination?: PaginationParams) => {
    const filterParams: Record<string, string | undefined> = {};
    if (filters?.location_id) filterParams.location_id = String(filters.location_id);
    if (filters?.product_equivalency_id) filterParams.product_equivalency_id = String(filters.product_equivalency_id);
    return api.get<PaginatedResponse<LocationProductEquivalency>>('/location_product_equivalencies', { params: withPagination(filterParams, pagination) }).then(res => res.data);
  },
  getOne: (id: number) =>
    api.get<LocationProductEquivalency>(`/location_product_equivalencies/${id}`).then(res => res.data),
  create: (data: { location_id: number; product_equivalency_id: number; max_count?: number; status?: string; notes?: string }) =>
    api.post<LocationProductEquivalency>('/location_product_equivalencies', { setting: data }).then(res => res.data),
  update: (id: number, data: Partial<{ max_count: number | null; position: number | null; status: string; notes: string; member_visibility_overrides: Record<string, { hide_when_zero: boolean } | null> }>) =>
    api.put<LocationProductEquivalency>(`/location_product_equivalencies/${id}`, { setting: data }).then(res => res.data),
  delete: (id: number) =>
    api.delete(`/location_product_equivalencies/${id}`).then(res => res.data),
  byLocation: (locationId: number) =>
    api.get<LocationProductEquivalency[]>('/location_product_equivalencies/by_location', { params: { location_id: locationId } }).then(res => res.data),
  maxForSku: (locationId: number, sku: string) =>
    api.get<{ max_count: number | null; product_equivalency_id: number | null }>('/location_product_equivalencies/max_for_sku', {
      params: { location_id: locationId, sku },
    }).then(res => res.data),
  upsert: (data: { location_id: number; product_equivalency_id: number; max_count?: number; status?: string; notes?: string }) =>
    api.post<LocationProductEquivalency>('/location_product_equivalencies/upsert', { setting: data }).then(res => res.data),
};

// HexaHQ Cache API
interface CacheStatus {
  count: number;
  last_synced_at: string | null;
}

interface CacheSyncResult {
  added: number;
  removed: number;
  updated: number;
  total: number;
  last_synced_at: string;
}

export interface HexahqSku {
  sku: string;
  name: string;
}

export interface HexahqAccount {
  hexahq_id: number;
  name: string;
}

export const hexahqCacheApi = {
  skuSearch: (q: string) => api.get<HexahqSku[]>('/hexahq_sku_cache', { params: { q } }).then(res => res.data),
  accountSearch: (q: string) => api.get<HexahqAccount[]>('/hexahq_account_cache', { params: { q } }).then(res => res.data),
  skuStatus: () => api.get<CacheStatus>('/hexahq_sku_cache/status').then(res => res.data),
  skuSync: () => api.post<CacheSyncResult>('/hexahq_sku_cache/sync').then(res => res.data),
  accountStatus: () => api.get<CacheStatus>('/hexahq_account_cache/status').then(res => res.data),
  accountSync: () => api.post<CacheSyncResult>('/hexahq_account_cache/sync').then(res => res.data),
};

// Add API version fetcher
export const systemApi = {
  getVersion: () => api.get<{ version: string }>('/version').then(res => res.data.version),
};

// Fleet dashboard API
export const fleetDashboardApi = {
  // `since` is an ISO timestamp filtering events_24h ONLY. publishes_24h is
  // always returned for the full 24h rolling window — see FleetDashboardService
  // for the rationale (cross-stream cursor drop on bucket boundaries).
  get: (since?: string) => {
    const params: Record<string, string> = {};
    if (since) params.since = since;
    return api.get<FleetDashboardPayload>('/dashboards/fleet', { params }).then(res => res.data);
  },
};

// Internal Ops dashboard API (sc-5800). Server-side cached; the kiosk page
// polls every 30s and the X-Kiosk-Token header is forwarded by the interceptor.
export const internalOpsDashboardApi = {
  // engagement selects the Engagement panel view (coverage | funnel | volume) —
  // see ?engagement=. Omitted => backend default (coverage).
  // acqGoals moves the Acquisition goal lines (?acq_quarterly_target etc.);
  // omitted keys => the shipped goals.
  get: (opts?: { engagement?: 'coverage' | 'funnel' | 'volume'; acqGoals?: AcquisitionGoalOverrides }) => {
    const params: Record<string, string> = {};
    if (opts?.engagement) params.engagement = opts.engagement;
    for (const key of ACQUISITION_GOAL_PARAMS) {
      const value = opts?.acqGoals?.[key];
      if (value) params[key] = value;
    }
    return api.get<InternalOpsDashboardPayload>('/dashboards/internal_ops', { params }).then(res => res.data);
  },
};

// Per-device comment chain
export const deviceCommentsApi = {
  list: (deviceId: number) =>
    api.get<DeviceCommentsResponse>(`/devices/${deviceId}/comments`).then(res => res.data.comments),
  append: (deviceId: number, body: string) =>
    api.post<{ comment: DeviceComment }>(`/devices/${deviceId}/comments`, { body }).then(res => res.data.comment),
};

// --- Edge Reviews (Outcomes) — demand-driven max recommendations [sc-5828] ---

export type RecommendationDirection = 'set' | 'raise' | 'lower';
// Order history's section grouping, assigned server-side (sc-5939). The PDF groups
// from the batch's proposal since sc-6441.
export type DisplayGroup = 'raises' | 'sets' | 'decreases' | 'dont_stock' | 'no_action';
export type RecommendationStatus = 'open' | 'completed' | 'declined' | 'expired';
// 'mass_flow' is the sc-5875 signal; 'drawdown'/'orders' remain only for
// pre-Rev-5 rows that still render gracefully.
export type RecommendationBasis = 'mass_flow' | 'drawdown' | 'orders';

// Mass-flow reference points + valuation, shared by open recs and review-group
// rows (sc-5875). All nullable. Review groups carry the sc-6440 engine fields
// declared on ReviewGroup (a rec carries them only inside `basis`).
export interface MassFlowFields {
  t_rate?: number | null;       // the turnover term (floored usage over the per-org window)
  rec?: number | null;          // the engine's max at the current cadence; 0 = occasional use / retire
  // sc-5986 whole-box context: every group-grain quantity here is UNITS
  // (a beacon = units_per_box units). For boxed groups the rec is snapped up
  // to a whole multiple of the preferred SKU's box size; rec_boxes is that box
  // count. Both null/absent for non-boxed groups and pre-sc-5986 payloads.
  hq_units_per_box?: number | null;
  rec_boxes?: number | null;
  demand_events?: number | null;
  tenure_weeks?: number | null;
  bis_present?: number | null;
  bis_departing?: number | null;
  dead_stock_units?: number | null;
  dead_stock_value_cents?: number | null;
  value_source?: 'window' | 'all_time' | 'fleet' | 'none' | null;
}

// What the sc-6440 engine decided for a group. too_new / monitoring carry no rec.
export type ReviewPolicy = 'stock' | 'order_as_needed' | 'retire' | 'too_new' | 'monitoring';

// The branch's restock cadence a review was computed at (sc-6440): current
// (restock_interval_weeks) vs recommended (median gap between order days).
// basis 'kept_current' = fewer than 3 order days, so the current is kept.
export interface ReviewCadence {
  current_weeks: number;
  recommended_weeks: number;
  basis: 'order_days' | 'kept_current';
  order_days: number;
  median_gap_weeks?: number | null; // sc-6441: the basis the recommended cadence was snapped from
}

// --- The batch at the SYNC (sc-6441) ---------------------------------------
// Every item at the branch, classified server-side at BOTH cadences (the
// current one and the recommended one), so the page re-derives on the cadence
// toggle by picking a side. Proposed kinds: up / down / new / occasional.
export type BatchKind = 'up' | 'down' | 'new' | 'occasional' | 'right' | 'quiet' | 'too_new';
export const PROPOSED_KINDS: BatchKind[] = ['up', 'down', 'new', 'occasional'];

export interface BatchSide {
  kind: BatchKind;
  engine_max: number | null; // the engine's max at this cadence (null on too-new rows)
  quiet: { status: 'completed' | 'declined'; since: string; until: string } | null;
}

export interface BatchRow {
  product_equivalency_id: number;
  name: string | null;
  sku: string | null;
  policy: ReviewPolicy | null;
  min: number | null;
  max_now: number | null; // the LIVE max (null = unset)
  units: number | null; // units used over the observed weeks
  demand_weeks: number | null; // weeks with use
  observed_weeks: number;
  tenure_weeks: number | null;
  on_hand: number | null;
  weeks: number[]; // units used per week across the review window, oldest first
  set_by: { name: string | null; email: string; on: string } | null; // latest max-change event
  recommendation_id: number | null; // the open recommendation, if any
  current: BatchSide;
  recommended: BatchSide;
}

export interface BatchAppliedRow {
  product_equivalency_id: number;
  name: string | null;
  outcome: 'applied' | 'kept' | 'left';
  before: number | null;
  after: number | null;
  engine_max: number | null;
  custom: boolean;
  note: string | null;
  recommendation_id: number | null;
}

// The record of the batch Apply wrote for this review — the table is
// read-only while it exists (until the next regeneration).
export interface BatchApplied {
  review_id: number;
  applied_at: string;
  applied_on: string;
  actor: string;
  actor_name: string | null;
  cadence: { from: number; to: number } | null;
  rows: BatchAppliedRow[];
  counts: { max_changes: number; from_customer: number; kept: number; left: number };
}

export interface ReviewBatch {
  review_id: number;
  window_weeks: number;
  cadence: { current_weeks: number; recommended_weeks: number; median_gap_weeks: number | null };
  windows: { quiet_weeks: number; kept_weeks: number; too_new_weeks: number; unused_weeks: number };
  rows: BatchRow[];
  // The branch's digest recipients: the To / Cc on the email Marah copies
  // after Apply (the app sends nothing).
  recipients: { to: string | null; cc: string[] };
  applied: BatchApplied | null;
}

export interface ReviewTerm {
  term: string;
  text: string;
}

export interface BatchApplyRequest {
  review_id: number;
  cadence_change: boolean;
  // Every row as the page saw it (kind = at the chosen cadence); new_max null = the starting number.
  rows: {
    product_equivalency_id: number; max_now: number | null; kind: BatchKind; new_max: number | null; included: boolean;
    note: string | null;
  }[];
}

export type BatchApplyResponse = OutcomesReview & {
  apply_result: {
    skipped: { product_equivalency_id: number; name: string | null; recommendation_id: number }[];
  };
};

export interface DeadStockSummary {
  value_cents: number;
  // Dead units we couldn't price (no realized order line) — surfaced rather than
  // silently dropped from the total (sc-5916). Absent on pre-sc-5916 rows.
  unpriced_units?: number | null;
}

export interface OutcomesLocationSummary {
  location_id: number;
  name: string;
  organization: { id: number; name: string } | null;
  open_counts: { set: number; raise: number; lower: number };
  keep_count: number;
  suppressed_count: number;
  still_maturing_count: number;
  dead_stock_value_cents: number | null;
  avoidable_expediting_value_cents: number | null; // sc-5939, per-location list total
  last_reviewed_on: string | null;
  computed_at: string | null;
}

// Fleet digest-acceptance summaries for the index's digest columns (sc-6100),
// classified by the same sc-6064 classifier as the location detail's Digest
// Outcomes section (the mixes reconcile exactly). digest_count is a LOCAL
// read — real even when summary is null; a null summary means "couldn't
// classify" (unlinked account, or order_data_available false fleet-wide) and
// renders as —, never as a fabricated 0%.
export interface FleetDigestAcceptanceRow {
  location_id: number;
  account_linked: boolean;
  digest_count: number;
  summary: Record<string, number> | null; // seven sc-6064 classes -> count
}

export interface FleetDigestAcceptance {
  since: string; // window start (last 6 months, clamped to the backend's HISTORY_FLOOR — 2026-03-10 since sc-6125)
  order_data_available: boolean; // ONE bulk replica sweep -> one flag
  locations: FleetDigestAcceptanceRow[];
}

export interface RecommendationHistoryEvent {
  event_type: string;
  actor: string | null;
  payload: Record<string, any>;
  occurred_at: string | null;
}

export interface OutcomesRecommendation extends MassFlowFields {
  id: number;
  group: { product_equivalency_id: number; name: string | null; hexa_hq_sku: string | null };
  direction: RecommendationDirection;
  status: RecommendationStatus;
  recommended_value: number;
  canonical_basis: RecommendationBasis;
  current_max: number | null;
  why: string | null;
  basis: Record<string, any>;
  last_computed_at: string | null;
  delivered_at: string | null;
  created_at: string | null;
  completed_at: string | null;
  applied_value: number | null;
  declined_at: string | null;
  declined_by: string | null;
  decline_note: string | null;
  expired_at: string | null;
  expired_reason: string | null;
  // sc-5939: levels-table grouping + the displayed avoidable-expediting cost (the
  // truck-mode rate is applied server-side; the page never sees the raw freight).
  display_group?: DisplayGroup | null;
  expediting_cost_cents?: number | null;
  history: RecommendationHistoryEvent[];
}

// One candidate group's derived outcome from the latest review (the
// review row payload) — keeps, suppressed, and still-monitoring entries.
// Typed to what the UI consumes; the payload carries further diagnostics
// (track stats, suppression refs) not declared here.
export interface ReviewGroup extends MassFlowFields {
  product_equivalency_id: number;
  group_name: string | null;
  hexa_hq_sku?: string | null;
  outcome: string;
  canonical_basis?: RecommendationBasis | null;
  current_max?: number | null;     // the CONFIGURED max at compute (nil = unset) — not max_current
  why?: string | null;
  // sc-6440 engine fields (absent on pre-sc-6440 reviews).
  policy?: ReviewPolicy | null;
  min?: number | null;             // p95 weekly demand
  max_current?: number | null;     // the RECOMMENDED max at the current cadence (= rec) — not current_max
  max_recommended?: number | null; // max at the recommended cadence
  demand_weeks?: number | null;
  total_demand_units?: number | null;
}

export interface HeatmapCell {
  date: string;
  // sc-5867: column identity (per-order, not per-day) + per-order expedited
  // flag. Both absent on reviews persisted before the feature — the heatmap
  // falls back to date-keyed columns and treats missing expedited as false.
  order_id?: number;
  sku: string;
  qty: number;
  // sc-5986: qty × the SKU's units_per_box — the grain group-row aggregation
  // uses (group-grain numbers are units). Absent on pre-sc-5986 payloads;
  // consumers fall back to qty (identical for non-boxed SKUs).
  units_qty?: number | null;
  restock_matched: boolean;
  expedited?: boolean;
}

// One heatmap row's metadata, group-grain (sc-5875 Step 1). group_key is the
// pe_id when grouped, else the raw SKU (ungrouped). The informational
// fold-partition marker: 'missing_max' (*, grouped + in the fold + no positive
// max) or 'not_in_edge' (✕, ordered + not in the fold). Unordered rows have
// ordered=false and no cells; bis_present/departing are null when unsensed.
export interface GroupRow {
  group_key: number | string;
  product_equivalency_id: number | null;
  name: string | null;
  ordered: boolean;
  sensed: boolean;
  bis_present: number | null;
  bis_departing: number | null;
  flag: 'missing_max' | 'not_in_edge' | null;
}

// Lazy RAW drawdown series for one group's heatmap row (sc-5875 Step 1): the
// raw daily snapshot on-hand, with covered_dates (location-wide) so a genuine 0
// is distinguishable from a sensor gap. Order points are NOT here — the client
// plots them from the already-loaded heatmap cells. Empty series => no sensor data.
export interface GroupChart {
  product_equivalency_id: number;
  since: string;
  until: string;
  current_max: number | null; // live configured group max — the chart's horizontal reference line
  recommended_max: number | null; // live open recommendation value — the chart's "rec" line (when it differs from current_max)
  consumption: { date: string; qty: number }[]; // weekly mass-flow consumption (Monday-keyed) the rec was computed from
  series: { date: string; on_hand: number }[];
  covered_dates: string[];
}

// Location HVA roll-up chart (sc-6057): the whole location's daily on-hand in
// BOXES (raw beacon counts — deliberately unweighted; the max ceiling is the
// unit-denominated LPE maxes converted to boxes instead, so both axes share
// one physical grain), plus HVA conversion dates and the go-live anchor.
// `since` doubles as the go-live marker: the chart starts AT go-live, or at
// the first snapshot when the registry has none (go_live_assumed). Degrades
// like the HVA rate card: unlinked account / unavailable replica => explicit
// flags, markers omitted, never an error.
export interface InventoryChart {
  since: string | null; // chart start == go-live marker; null => nothing to chart
  until: string;
  go_live_assumed: boolean;
  max_capacity_boxes: number | null;
  // "If recs applied" ceiling (sc-6127): the same box-grain universe with
  // every open rec's value substituted for its group max. Equals
  // max_capacity_boxes when nothing is open — the chart draws one line then.
  rec_capacity_boxes: number | null;
  account_linked: boolean;
  order_markers_available: boolean; // one flag for BOTH marker sets (they degrade together)
  hva_order_dates: string[]; // Central dates, in-range, deduped
  non_hva_order_dates: string[]; // qualifying orders that did NOT convert from a digest quote
  series: { date: string; on_hand: number }[];
  covered_dates: string[];
}

// Digest Outcomes (sc-6064): per sent restock digest, HOW the customer acted —
// the acceptance class plus per-line accepted/reduced/omitted/extras detail and
// the contributing HQ orders. Read-time endpoint (nothing cached in the review
// payload). Quantities are the labeled SKU's box grain (`boxes`, bare in cells);
// `units` carries the sc-5986 long form for tooltips. Degrades like the HVA
// surfaces: explicit account_linked / order_data_available flags, never zeros.
export interface DigestOutcomeLine {
  sku: string;
  boxes: number;
  units: number;
  over_boxes?: number; // accepted lines ordered above rec ("extra qty", not a class)
  rec_boxes?: number; // reduced lines: the recommended qty in the same box grain
  rec_units?: number;
}
export interface DigestOutcomeRow {
  digest_id: number;
  sent_at: string;
  outcome: string; // accepted_as_is | accepted_indirect | accepted_with_additions | quantity_reduced | cherry_picked | late_action | no_action
  accepted_at: string | null; // FIRST qualifying order (responsiveness); per-order times in `orders`
  accepted_same_day: boolean | null; // Central same-day => blue date, later => amber
  accepted: DigestOutcomeLine[];
  reduced: DigestOutcomeLine[];
  omitted: DigestOutcomeLine[];
  extras: { sku: string; quantity: number }[];
  orders: { id: number; at: string; url: string | null }[]; // url env-aware from the backend; null => render plain text
}
export interface DigestOutcomes {
  since: string;
  account_linked: boolean;
  order_data_available: boolean;
  summary: Record<string, number>; // class -> count over the window
  digests: DigestOutcomeRow[]; // newest first
  most_omitted: { sku: string; recommended: number; omitted: number; reduced: number; miss_rate: number }[];
}

// --- Order pattern (sc-6127): the read-time block on /review feeding the
// combined decision table's day cells, each row's digest-ask rail, the day
// drawer, and the Expedited $ column. All quantities are UNITS (group-grain
// numbers are units, sc-5986); group keys follow the group_rows convention
// (pe_id when grouped, else the raw SKU) and arrive stringified as JSON keys.

export interface OrderPatternLine {
  sku: string;
  name: string | null;
  quantity: number; // HQ box grain (the drawer's Qty column)
  units: number;
  value_cents: number;
  group_key: number | string;
}

export interface OrderPatternOrder {
  id: number;
  quote_id: number | null;
  date: string; // Central placement date — the day-bin key
  method: string | null;
  expedited: boolean;
  // In-window matching digest response (the classifier's business-day
  // deadline) -> blue; late responses and ad-hoc orders alike -> orange.
  by_deadline: boolean;
  from_digest: boolean; // direct linkage: the order's quote spun from a digest
  desired_date: string | null;
  delivered_date: string | null;
  origin: string | null;
  freight_cents: number;
  hq_url: string | null; // env-aware; null => render plain text
  value_cents: number;
  lines: OrderPatternLine[];
}

export interface DigestAsk {
  digest_id: number;
  date: string; // Central send date
  // As-sent group name — names the synthesized row when an asked-about group
  // has left the review spine (an ignored digest must never vanish).
  group_name: string | null;
  asked_units: number;
  ordered_units: number; // total attributed before the next digest
  order_count: number; // contributing orders (>1 = split)
  late: boolean; // some contribution landed after the deadline
  outcome: 'as_sent' | 'off_plan' | 'ignored';
}

export interface OrderPattern {
  account_linked: boolean;
  order_data_available: boolean;
  since: string;
  orders: OrderPatternOrder[];
  asks: Record<string, DigestAsk[]>; // String(group_key) -> send-ordered asks
  expedited_premium_cents: Record<string, number>; // String(group_key) -> cents
  totals: { premium_cents: number; order_count: number; expedited_order_count: number };
}

export interface OutcomesReview {
  location_id: number;
  name: string;
  reviewed: boolean;
  run_on: string | null;
  computed_at: string | null;
  orders_available: boolean | null;
  uses_stock_truck: boolean;
  cadence?: ReviewCadence | null; // sc-6440; null on never-reviewed + pre-sc-6440 reviews
  turnover_window_days: number; // the turnover-cap window (per-org, sc-5875)
  summary: Record<string, number>;
  dead_stock: DeadStockSummary | null;
  // Displayed avoidable-expediting cost, per-location per-class rates already
  // applied (sc-5939/sc-5979). The block itself is always present (value_cents 0
  // when nothing carries freight); only the COUNT fields are null on pre-sc-5979
  // review payloads — gate count display on the counts, not the block.
  // total_order_count has no TSX consumer (the PDF reads it server-side); typed
  // for contract accuracy.
  avoidable_expediting_cost: {
    value_cents: number;
    total_order_count?: number | null;
    expedited_order_count?: number | null;
    will_call_order_count?: number | null;
  } | null;
  recommendations: OutcomesRecommendation[];
  keeps: ReviewGroup[];
  suppressed: ReviewGroup[];
  still_monitoring: ReviewGroup[];
  recent_terminal: OutcomesRecommendation[];
  heatmap: HeatmapCell[];
  // Group-grain heatmap (sc-5875 Step 1). Both empty/absent on pre-feature rows
  // -> the heatmap falls back to SKU-grain, unmarked.
  sku_group_keys: Record<string, number>; // sku -> pe_id (sparse; ungrouped SKUs absent)
  group_rows: GroupRow[];
  // Read-time order pattern (sc-6127) — always present on /review and
  // /regenerate responses (degraded via its own flags, never absent).
  order_pattern: OrderPattern;
  // sc-6441: the branch's LIVE restock cadence (moves on Apply), the batch
  // table (null with no current-engine review), and the page's definitions.
  restock_interval_weeks: number;
  // The LIVE group maxes, pe_id -> max (0 kept; an unset max is absent):
  // Order history's Current max, current before and after Apply.
  live_maxes: Record<string, number>;
  batch: ReviewBatch | null;
  definitions: ReviewTerm[];
}

export const outcomesApi = {
  locations: () =>
    api.get<OutcomesLocationSummary[]>('/outcomes/locations').then(res => res.data),
  // Fleet digest-acceptance summaries for the index columns [sc-6100] —
  // fetched in parallel with `locations` and merged client-side, so the index
  // render never waits on the replica sweep.
  digestAcceptance: () =>
    api.get<FleetDigestAcceptance>('/outcomes/locations/digest_acceptance').then(res => res.data),
  review: (locationId: number | string) =>
    api.get<OutcomesReview>(`/outcomes/locations/${locationId}/review`).then(res => res.data),
  // On-demand full regeneration for one location: returns the fresh
  // review (same shape as `review`) so the page updates without a refetch.
  regenerate: (locationId: number | string) =>
    api.post<OutcomesReview>(`/outcomes/locations/${locationId}/regenerate`).then(res => res.data),
  // The batch at the SYNC [sc-6441]: returns the fresh review with apply_result.
  batchApply: (locationId: number | string, body: BatchApplyRequest) =>
    api.post<BatchApplyResponse>(`/outcomes/locations/${locationId}/batch_apply`, body).then(res => res.data),
  // Lazy raw drawdown series for one group's heatmap row [sc-5875 Step 1].
  groupChart: (locationId: number | string, peId: number) =>
    api.get<GroupChart>(`/outcomes/locations/${locationId}/groups/${peId}/chart`).then(res => res.data),
  // Location HVA roll-up chart [sc-6057].
  inventoryChart: (locationId: number | string) =>
    api.get<InventoryChart>(`/outcomes/locations/${locationId}/inventory_chart`).then(res => res.data),
  // Digest Outcomes acceptance mix [sc-6064].
  digestOutcomes: (locationId: number | string) =>
    api.get<DigestOutcomes>(`/outcomes/locations/${locationId}/digest_outcomes`).then(res => res.data),
  // Customer-facing Edge Review PDF [sc-5916]: blob GET via the shared axios
  // instance so it carries the bearer token (a raw browser download would not).
  // Returns the bytes + the server's filename for an object-URL download.
  reportPdf: async (locationId: number | string): Promise<{ blob: Blob; filename: string }> => {
    const res = await api.get(`/outcomes/locations/${locationId}/report.pdf`, { responseType: 'blob' });
    const disposition = (res.headers['content-disposition'] as string | undefined) ?? '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return { blob: res.data as Blob, filename: match?.[1] ?? `edge-review-${locationId}.pdf` };
  },
};

// Edge Onboard admin API (webhook-server engines/edge_onboard, /edge_onboard/admin/*).
// Small datasets (dozens of go-lives), so index endpoints return bare arrays —
// no pagination wrapper, filtering happens client-side beyond the query params.
export const edgeOnboardApi = {
  customerUsers: {
    getAll: (filters?: { location_id?: number; include_deleted?: boolean }) => {
      const params: Record<string, string> = {};
      if (filters?.location_id) params.location_id = String(filters.location_id);
      if (filters?.include_deleted) params.include_deleted = 'true';
      return api
        .get<EdgeOnboardCustomerUser[]>('/edge_onboard/admin/customer_users', { params })
        .then(res => res.data);
    },
    getOne: (id: number) =>
      api.get<EdgeOnboardCustomerUser>(`/edge_onboard/admin/customer_users/${id}`).then(res => res.data),
    create: (data: EdgeOnboardCustomerUserInput) =>
      api
        .post<EdgeOnboardCustomerUser>('/edge_onboard/admin/customer_users', { customer_user: data })
        .then(res => res.data),
    update: (id: number, data: EdgeOnboardCustomerUserUpdate) =>
      api
        .patch<EdgeOnboardCustomerUser>(`/edge_onboard/admin/customer_users/${id}`, { customer_user: data })
        .then(res => res.data),
    delete: (id: number) => api.delete(`/edge_onboard/admin/customer_users/${id}`).then(res => res.data),
    restore: (id: number) =>
      api
        .post<EdgeOnboardCustomerUser>(`/edge_onboard/admin/customer_users/${id}/restore`)
        .then(res => res.data),
  },

  // Engine-wide singleton settings (sc-6383). PATCH is a full replace of the
  // slack_mentions list; the server 422s with { errors: [...] } on bad input.
  settings: {
    get: () => api.get<EdgeOnboardSettings>('/edge_onboard/admin/settings').then(res => res.data),
    update: (data: { slack_mentions: EdgeOnboardSlackMention[] }) =>
      api.patch<EdgeOnboardSettings>('/edge_onboard/admin/settings', data).then(res => res.data),
  },

  goLives: {
    getAll: (filters?: { state?: EdgeOnboardGoLiveState; location_id?: number }) => {
      const params: Record<string, string> = {};
      if (filters?.state) params.state = filters.state;
      if (filters?.location_id) params.location_id = String(filters.location_id);
      return api.get<EdgeOnboardGoLive[]>('/edge_onboard/admin/go_lives', { params }).then(res => res.data);
    },
    getOne: (id: number) =>
      api.get<EdgeOnboardGoLiveDetail>(`/edge_onboard/admin/go_lives/${id}`).then(res => res.data),
    create: (data: { location_id: number; customer_user_id: number }) =>
      api.post<EdgeOnboardGoLive>('/edge_onboard/admin/go_lives', { go_live: data }).then(res => res.data),
    update: (id: number, data: { customer_user_id: number }) =>
      api.patch<EdgeOnboardGoLive>(`/edge_onboard/admin/go_lives/${id}`, { go_live: data }).then(res => res.data),
    delete: (id: number) => api.delete(`/edge_onboard/admin/go_lives/${id}`).then(res => res.data),
    complete: (id: number) =>
      api.post<EdgeOnboardGoLive>(`/edge_onboard/admin/go_lives/${id}/complete`).then(res => res.data),
    reopen: (id: number) =>
      api.post<EdgeOnboardGoLive>(`/edge_onboard/admin/go_lives/${id}/reopen`).then(res => res.data),
  },

  submissions: {
    getAll: (goLiveId: number) =>
      api
        .get<EdgeOnboardSubmission[]>(`/edge_onboard/admin/go_lives/${goLiveId}/submissions`)
        .then(res => res.data),
    review: (goLiveId: number, id: number) =>
      api
        .patch<EdgeOnboardSubmission>(`/edge_onboard/admin/go_lives/${goLiveId}/submissions/${id}/review`)
        .then(res => res.data),
    unreview: (goLiveId: number, id: number) =>
      api
        .patch<EdgeOnboardSubmission>(`/edge_onboard/admin/go_lives/${goLiveId}/submissions/${id}/unreview`)
        .then(res => res.data),
    // Staff override for the customer confirm step: this submission's Pending
    // slips → Assigned. Unsticks go-lives where the customer tagged items but
    // never reached the confirmation screen.
    confirmSlips: (goLiveId: number, id: number) =>
      api
        .post<EdgeOnboardSubmission>(
          `/edge_onboard/admin/go_lives/${goLiveId}/submissions/${id}/confirm_slips`,
        )
        .then(res => res.data),
    // Onboarding → inventory bridge: beacon assignments for the submission's
    // confirmed tags + LPA upsert. 409 lists tags owned by another product.
    assignProduct: (goLiveId: number, id: number, sku: string) =>
      api
        .post<EdgeOnboardSubmission>(
          `/edge_onboard/admin/go_lives/${goLiveId}/submissions/${id}/assign_product`,
          { sku },
        )
        .then(res => res.data),
    // Undo — destroys the beacon assignments created from this submission.
    // 422 once any tag is reporting sensor data (live inventory).
    unassignProduct: (goLiveId: number, id: number) =>
      api
        .post<EdgeOnboardSubmission>(
          `/edge_onboard/admin/go_lives/${goLiveId}/submissions/${id}/unassign_product`,
        )
        .then(res => res.data),
  },

  tagSlips: {
    getAll: (goLiveId: number) =>
      api
        .get<EdgeOnboardTagSlip[]>(`/edge_onboard/admin/go_lives/${goLiveId}/tag_slips`)
        .then(res => res.data),
    // Body shape: { tag_slips: [{ mac_address }, ...] } — server assigns slip_ids
    // sequentially, lowercases MACs, 409s on a global MAC collision. The create
    // response is a slim shape (not the full TagSlip the index returns).
    createBulk: (goLiveId: number, macAddresses: string[]) =>
      api
        .post<{ tag_slips: Pick<EdgeOnboardTagSlip, 'id' | 'slip_id' | 'mac_address' | 'state'>[] }>(
          `/edge_onboard/admin/go_lives/${goLiveId}/tag_slips`,
          { tag_slips: macAddresses.map(mac_address => ({ mac_address })) },
        )
        .then(res => res.data.tag_slips),
    downloadMacsCsv: (goLiveId: number) =>
      api
        .get<Blob>(`/edge_onboard/admin/go_lives/${goLiveId}/tag_slips/macs_csv`, { responseType: 'blob' })
        .then(res => res.data),
  },
};
